import { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { ChevronDownIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import useJobAssemblyTimers from "../../hooks/useJobAssemblyTimers";
import usePartAssembly from "../../data/usePartAssembly";
import { IPartAssembly, IPartAssemblyPartCategories } from "../../models/Part";
import { JobPartCategory } from "../../models/Job";
import JobPart from "./JobPart";
import LoadingWheel from "../shared/LoadingWheel";
import PinProtect, { PinProtectState } from "../shared/PinProtect";
import formatDateTime from "../../utils/formatDateTime";

// Component to handle indeterminate checkbox state
function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  className
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  className?: string;
}) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      type="checkbox"
      ref={checkboxRef}
      checked={checked}
      onChange={onChange}
      className={className}
    />
  );
}

export const HIDDEN_ASSEMBLIES_STORAGE_KEY = "jobPartsHiddenAssemblies";
const NON_ASSEMBLY_PARTS_ID = "00000000-0000-0000-0000-000000000000";

export const readHiddenAssemblyIds = (): string[] => {
  try {
    const saved = localStorage.getItem(HIDDEN_ASSEMBLIES_STORAGE_KEY);
    if (!saved) return [];

    const parsed = JSON.parse(saved);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
};

export interface JobAssemblyWithParts extends IPartAssembly {
  jobParts: JobPartCategory[];
}

export interface JobPartsByAssemblyProps {
  jobId?: string;
  jobPartCategories: JobPartCategory[];
  markAsRemoved: (partData: JobPartCategory['partData']) => void;
  setDamaged: (partData: JobPartCategory['partData']) => void;
  setDamagedOpen: (open: boolean) => void;
  handlePrint: (partData: JobPartCategory['partData']) => void;
  selectedPartIds: Set<string>;
  togglePartSelection: (partId: string | null | undefined) => void;
  toggleAssemblySelection: (assemblyParts: JobPartCategory[]) => void;
  onDiagramClick?: (partCategory: JobPartCategory) => void;
  /**
   * When set, only the assembly with this id is shown (used by Station Manager
   * to focus a single station). The hidden-assembly controls and the
   * Non-Assembly parts section are suppressed in this mode.
   */
  restrictToAssemblyId?: string;
}

export default function JobPartsByAssembly({
  jobId,
  jobPartCategories,
  markAsRemoved,
  setDamaged,
  setDamagedOpen,
  handlePrint,
  selectedPartIds,
  togglePartSelection,
  toggleAssemblySelection,
  onDiagramClick,
  restrictToAssemblyId,
}: JobPartsByAssemblyProps) {
  const {
    sessionRows,
    isAssemblyActionLoading,
    handleAssemblyStart,
    handleAssemblyPause,
    handleAssemblyFinish,
  } = useJobAssemblyTimers(jobId);
  const { assemblies } = usePartAssembly();
  const [isLoading, setIsLoading] = useState(true);
  const [assemblyGroups, setAssemblyGroups] = useState<JobAssemblyWithParts[]>([]);
  const [unmatchedParts, setUnmatchedParts] = useState<JobPartCategory[]>([]);
  const [now, setNow] = useState<number>(Date.now());
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinState, setPinState] = useState<PinProtectState>();
  const [collapsedAssemblies, setCollapsedAssemblies] = useState<Record<string, boolean>>({});
  const [hiddenAssemblyIds, setHiddenAssemblyIds] = useState<string[]>(readHiddenAssemblyIds);
  const [pendingCollapseAssemblyId, setPendingCollapseAssemblyId] = useState<string | null>(null);
  const hasInitialCollapseStateLoadedRef = useRef(false);
  const previousCompletionStateRef = useRef<Record<string, boolean>>({});
  const previousActiveSessionStateRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatDuration = (seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(safeSeconds / 3600).toString().padStart(2, "0");
    const minutes = Math.floor((safeSeconds % 3600) / 60).toString().padStart(2, "0");
    const remainingSeconds = Math.floor(safeSeconds % 60).toString().padStart(2, "0");

    return `${hours}:${minutes}:${remainingSeconds}`;
  };

  const parseApiUtcDate = (value: string) => DateTime.fromISO(value, { zone: "utc" });
  const isPartCompleted = (part: JobPartCategory) => !!part.partData?.removedOn || !!part.partData?.isDamaged;
  const isAssemblyCompleted = (assembly: JobAssemblyWithParts) =>
    assembly.jobParts.length > 0 && assembly.jobParts.every(isPartCompleted);

  useEffect(() => {
    if (!assemblies.isLoading && jobPartCategories) {
      // Find matching parts for each assembly
      const matchedAssemblies: JobAssemblyWithParts[] = assemblies.data?.data.map((assembly: IPartAssembly) => {
        // Create a map of categoryId to order for quick lookup
        const categoryOrderMap = new Map(
          assembly.partAssemblyPartCategories?.map((cat, index) => [cat.partCategoryId, index]) ?? []
        );

        // Filter and sort job parts based on the order in partAssemblyPartCategories
        const matchingJobParts: JobPartCategory[] = jobPartCategories
          .filter(jobPart =>
            assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) =>
              cat.partCategoryId === jobPart.partCategoryId
            )
          )
          .sort((a, b) => {
            const orderA = categoryOrderMap.get(a.partCategoryId!) ?? Number.MAX_SAFE_INTEGER;
            const orderB = categoryOrderMap.get(b.partCategoryId!) ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

        return {
          ...assembly,
          jobParts: matchingJobParts,
        };
      }) ?? [];

      // Filter out job parts that have no matching assemblies
      const unmatchedJobParts = jobPartCategories.filter(jobPart =>
        !matchedAssemblies?.some(assembly =>
          assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) =>
            cat.partCategoryId === jobPart.partCategoryId
          )
        )
      );

      setAssemblyGroups(matchedAssemblies.filter(assembly => assembly.jobParts.length > 0));
      setUnmatchedParts(unmatchedJobParts);
      setIsLoading(false);
    }
  }, [assemblies.isLoading, assemblies.data, jobPartCategories]);

  useEffect(() => {
    if (!assemblyGroups.length) return;

    const completionByAssembly: Record<string, boolean> = {};
    const activeSessionByAssembly: Record<string, boolean> = {};

    assemblyGroups.forEach((assembly) => {
      completionByAssembly[assembly.id] = isAssemblyCompleted(assembly);
      activeSessionByAssembly[assembly.id] = sessionRows.some(
        (session) => session.partAssemblyId === assembly.id && !session.dismantlingEnd
      );
    });

    setCollapsedAssemblies((prev) => {
      const next = { ...prev };

      if (!hasInitialCollapseStateLoadedRef.current) {
        assemblyGroups.forEach((assembly) => {
          if (next[assembly.id] === undefined) {
            next[assembly.id] = completionByAssembly[assembly.id];
          }
        });
        hasInitialCollapseStateLoadedRef.current = true;
      } else {
        assemblyGroups.forEach((assembly) => {
          const wasCompleted = previousCompletionStateRef.current[assembly.id] ?? false;
          const isCompleted = completionByAssembly[assembly.id];
          const wasActiveSession = previousActiveSessionStateRef.current[assembly.id] ?? false;
          const hasActiveSession = activeSessionByAssembly[assembly.id];

          if (!wasCompleted && isCompleted && !hasActiveSession) {
            next[assembly.id] = true;
          } else if (wasActiveSession && !hasActiveSession && isCompleted) {
            next[assembly.id] = true;
          } else if (next[assembly.id] === undefined) {
            next[assembly.id] = isCompleted;
          }
        });
      }

      return next;
    });

    previousCompletionStateRef.current = completionByAssembly;
    previousActiveSessionStateRef.current = activeSessionByAssembly;
  }, [assemblyGroups, sessionRows]);

  useEffect(() => {
    if (!pendingCollapseAssemblyId) return;

    const targetAssembly = assemblyGroups.find((assembly) => assembly.id === pendingCollapseAssemblyId);
    if (!targetAssembly) {
      setPendingCollapseAssemblyId(null);
      return;
    }

    // Wait until the stop action has actually settled (timer no longer active)
    // before deciding whether to collapse. Don't clear the pending id while the
    // session is still active, otherwise the refetch from finish/pause never
    // gets a chance to trigger the collapse.
    const hasActiveSession = sessionRows.some(
      (session) => session.partAssemblyId === pendingCollapseAssemblyId && !session.dismantlingEnd
    );

    if (hasActiveSession) return;

    if (isAssemblyCompleted(targetAssembly)) {
      setCollapsedAssemblies((prev) => ({
        ...prev,
        [pendingCollapseAssemblyId]: true,
      }));
    }

    setPendingCollapseAssemblyId(null);
  }, [pendingCollapseAssemblyId, assemblyGroups, sessionRows]);

  if (isLoading) {
    return <LoadingWheel />;
  }

  const openPausePin = (assemblyId: string, assemblyName: string) => {
    const reason = window.prompt(`Pause reason for ${assemblyName}`);
    if (!reason || !reason.trim()) return;

    setPinModalOpen(true);
    setPinState({
      pinLabel: `Pause ${assemblyName}`,
      onPinSuccess: (pinUserId) => {
        if (!pinUserId) return;
        setPendingCollapseAssemblyId(assemblyId);
        handleAssemblyPause(assemblyId, reason.trim(), pinUserId);
      },
      onPinFailed: () => {
      },
    });
  };

  const hideAssembly = (assemblyId: string) => {
    setHiddenAssemblyIds((prev) => {
      if (prev.includes(assemblyId)) return prev;

      const next = [...prev, assemblyId];
      localStorage.setItem(HIDDEN_ASSEMBLIES_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const showAllHiddenAssemblies = () => {
    setHiddenAssemblyIds([]);
    localStorage.setItem(HIDDEN_ASSEMBLIES_STORAGE_KEY, JSON.stringify([]));
  };

  const visibleAssemblyGroups = restrictToAssemblyId
    ? assemblyGroups.filter((assembly) => assembly.id === restrictToAssemblyId)
    : assemblyGroups.filter((assembly) => !hiddenAssemblyIds.includes(assembly.id));

  const openFinishPin = (assemblyId: string, assemblyName: string) => {
    setPinModalOpen(true);
    setPinState({
      pinLabel: `Finish ${assemblyName}`,
      onPinSuccess: (pinUserId) => {
        if (!pinUserId) return;
        setPendingCollapseAssemblyId(assemblyId);
        handleAssemblyFinish(assemblyId, pinUserId);
      },
      onPinFailed: () => {
      },
    });
  };

  return (
    <>
      <PinProtect open={pinModalOpen} setOpen={setPinModalOpen} pinState={pinState} useAuthUser={false} />
      <div className="space-y-6">
      {!restrictToAssemblyId && hiddenAssemblyIds.length > 0 && (
        <button
          type="button"
          onClick={showAllHiddenAssemblies}
          className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50"
        >
          Show {hiddenAssemblyIds.length} hidden {hiddenAssemblyIds.length === 1 ? "assembly" : "assemblies"}
        </button>
      )}
      {/* Assembly Groups */}
      {visibleAssemblyGroups.map((assembly) => {
        const sessionsForAssembly = sessionRows.filter((session) => session.partAssemblyId === assembly.id);
        const activeSession = sessionsForAssembly.find((session) => !session.dismantlingEnd);
        const totalAssemblySeconds = sessionsForAssembly.reduce((total, session) => {
          if (!session.dismantlingStart) return total;

          const start = parseApiUtcDate(session.dismantlingStart);
          const end = session.dismantlingEnd
            ? parseApiUtcDate(session.dismantlingEnd)
            : DateTime.utc();

          const diff = Math.floor(end.diff(start, "seconds").seconds);
          return total + (diff > 0 ? diff : 0);
        }, 0);
        const sortedByActivity = [...sessionsForAssembly].sort((a, b) => {
          const aActivity = a.dismantlingEnd ?? a.dismantlingStart ?? a.created;
          const bActivity = b.dismantlingEnd ?? b.dismantlingStart ?? b.created;
          return parseApiUtcDate(bActivity).toMillis() - parseApiUtcDate(aActivity).toMillis();
        });
        const latestActivity = sortedByActivity[0];

        const assemblyPartIds = assembly.jobParts
          .map(jobPart => jobPart.partData?.id)
          .filter((id): id is string => id !== null && id !== undefined);
        const allSelected = assemblyPartIds.length > 0 && assemblyPartIds.every(id => selectedPartIds.has(id));
        const someSelected = assemblyPartIds.some(id => selectedPartIds.has(id));
        const isCollapsed = collapsedAssemblies[assembly.id] ?? false;
        const isCompleted = isAssemblyCompleted(assembly);

        return (
          <div key={assembly.id} className="rounded-lg bg-gray-100 p-4">
            <div className="flex flex-col gap-y-2 mb-4">
              <div className="flex items-center gap-2">
              <IndeterminateCheckbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={() => toggleAssemblySelection(assembly.jobParts)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <h3 className="text-lg font-semibold">
                {assembly.name}
                {isCompleted && <span className="ml-1 text-base text-green-700">- Completed</span>}
              </h3>
              {someSelected && (
                <span className="text-sm text-gray-600">
                  ({assemblyPartIds.filter(id => selectedPartIds.has(id)).length} of {assemblyPartIds.length} selected)
                </span>
              )}
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => hideAssembly(assembly.id)}
                  aria-label={`Hide ${assembly.name}`}
                  className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                >
                  <EyeSlashIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCollapsedAssemblies((prev) => ({
                      ...prev,
                      [assembly.id]: !isCollapsed,
                    }))
                  }
                  aria-label={isCollapsed ? `Expand ${assembly.name}` : `Collapse ${assembly.name}`}
                  className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                >
                  <ChevronDownIcon
                    className={`h-5 w-5 transition-transform duration-200 ease-in-out ${isCollapsed ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                <span className="rounded-full bg-white px-2 py-1 font-medium text-gray-700">
                  Total: {formatDuration(totalAssemblySeconds)}
                </span>
                {activeSession && (
                  <span className="rounded-full bg-green-100 px-2 py-1 font-medium text-green-700">
                    Active
                  </span>
                )}
                {latestActivity?.dismantlingStart && (
                  <span className="text-gray-600">
                    Last activity: {formatDateTime(latestActivity.dismantlingEnd ?? latestActivity.dismantlingStart, DateTime.DATETIME_SHORT)}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  {!activeSession ? (
                    <button
                      type="button"
                      className="rounded-md bg-primary-600 px-3 py-1 text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => handleAssemblyStart(assembly.id)}
                      disabled={isAssemblyActionLoading}
                    >
                      Start
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="rounded-md bg-amber-600 px-3 py-1 text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => openPausePin(assembly.id, assembly.name)}
                        disabled={isAssemblyActionLoading}
                      >
                        Pause
                      </button>
                      <button
                        type="button"
                        className="rounded-md bg-green-600 px-3 py-1 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => openFinishPin(assembly.id, assembly.name)}
                        disabled={isAssemblyActionLoading}
                      >
                        Finish
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            {!isCollapsed && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {assembly.jobParts.map((jobPart) => (
                  <JobPart
                    key={jobPart.partDataId}
                    partCategory={jobPart}
                    markAsRemoved={markAsRemoved}
                    setDamaged={setDamaged}
                    setDamagedOpen={setDamagedOpen}
                    handlePrint={handlePrint}
                    selectedPartIds={selectedPartIds}
                    togglePartSelection={togglePartSelection}
                    onDiagramClick={onDiagramClick}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Unmatched Parts */}
      {!restrictToAssemblyId && unmatchedParts.length > 0 && !hiddenAssemblyIds.includes(NON_ASSEMBLY_PARTS_ID) && (() => {
        const unmatchedPartIds = unmatchedParts
          .map(jobPart => jobPart.partData?.id)
          .filter((id): id is string => id !== null && id !== undefined);
        const allUnmatchedSelected = unmatchedPartIds.length > 0 && unmatchedPartIds.every(id => selectedPartIds.has(id));
        const someUnmatchedSelected = unmatchedPartIds.some(id => selectedPartIds.has(id));

        return (
          <div className="rounded-lg bg-gray-100 p-4">
            <div className="flex items-center gap-2 mb-4">
              <IndeterminateCheckbox
                checked={allUnmatchedSelected}
                indeterminate={someUnmatchedSelected && !allUnmatchedSelected}
                onChange={() => toggleAssemblySelection(unmatchedParts)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <h3 className="text-lg font-semibold">Non-Assembly Parts</h3>
              {someUnmatchedSelected && (
                <span className="text-sm text-gray-600">
                  ({unmatchedPartIds.filter(id => selectedPartIds.has(id)).length} of {unmatchedPartIds.length} selected)
                </span>
              )}
              <button
                type="button"
                onClick={() => hideAssembly(NON_ASSEMBLY_PARTS_ID)}
                aria-label="Hide Non-Assembly Parts"
                className="ml-auto rounded-md bg-white p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
              >
                <EyeSlashIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {unmatchedParts.map((jobPart) => (
                <JobPart
                  key={jobPart.id}
                  partCategory={jobPart}
                  markAsRemoved={markAsRemoved}
                  setDamaged={setDamaged}
                  setDamagedOpen={setDamagedOpen}
                  handlePrint={handlePrint}
                  selectedPartIds={selectedPartIds}
                  togglePartSelection={togglePartSelection}
                  onDiagramClick={onDiagramClick}
                />
              ))}
            </div>
          </div>
        );
      })()}
    </div>
    </>
  );
} 